/**
 * 이상한 방 - Three.js 씬 부트스트랩 (아이소메트릭)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 7.0절(스택 확정) · 원칙 2(1절)
 *
 * 이 파일의 책임은 둘이다.
 *  1. 렌더러·카메라·레이캐스터를 한 번 만들어 `SceneCtx`로 묶는다.
 *  2. **아이소메트릭 투영의 산수를 단독으로 소유한다** - 각도·화면 기저·방의 화면상 크기.
 *
 * 2번을 여기 둔 이유는 층위 때문이다. `input/isoCamera.ts`가 카메라를 조작하려면 같은 산수가 필요한데,
 * `render`가 `input`을 import 할 수는 없다(의존 방향이 뒤집힌다). 그렇다고 양쪽에 각자 두면
 * "화면에 방이 다 들어왔다고 계산하는 쪽"과 "실제로 그리는 쪽"이 갈려서,
 * 줌아웃했는데 방 귀퉁이가 잘리는 상태가 된다. 그건 원칙 2 위반이다.
 *
 * ## 왜 원근이 아니라 직교인가
 *
 * 1인칭에서는 화면 크기가 곧 정보량이라 폰에서 25cm짜리 변화를 볼 수 없었다.
 * 아이소메트릭 + 줌은 그 문제를 푼다 - **줌 레벨이 1인칭의 시야 차단을 대체한다.**
 * 대신 고정 시점은 원칙 2를 정면으로 위협하므로(한 각도에서 영원히 가려지는 물건 = 찾을 수 없는 정답)
 * 카메라 4방향 회전이 필수 장치가 된다. 그 조작은 `input/isoCamera.ts`가 가져간다.
 *
 * ## 광원은 여기에 없다
 *
 * 공간이 밀폐 실내(4벽 + 천장)이므로 조명은 평면도에 종속이다. 광원 전량은
 * `lighting.ts`의 `createLightingRig(scene, plan)`이 소유하고, 이 파일은 렌더러 설정만 책임진다.
 * 리그가 붙기 전 씬이 캄캄하면 조립 중 어느 단계가 깨졌는지 분간할 수 없으므로
 * `BOOTSTRAP_LIGHT_NAME` 이름의 임시 반구광 하나만 붙인다(리그가 그 이름을 보고 걷어낸다).
 *
 * 그림자 카메라도 마찬가지로 `lighting.ts`의 몫이며 **직교 전환의 영향을 받지 않는다** -
 * 그림자 절두체는 방 바운딩 스피어에 맞춰져 있지 뷰 카메라에 맞춰져 있지 않기 때문이다.
 * 뷰 카메라 쪽에서 이 파일이 책임지는 것은 근·원평면뿐이고, 그건 아래 `configureIsoCamera`에 있다.
 *
 * ## 톤매핑 - ACES를 쓰되 노출을 올리지 않는다 (중요)
 *
 * 목표 룩이 따뜻한 파스텔 실내이므로 하이라이트를 선형으로 클리핑하면 창가가 흰 판때기가 된다.
 * 대신 **노출은 1.0을 넘기지 않는다.** 팔레트(`palette.ts`)는 인접 색 명암비 1.33:1의 빠듯한
 * 사다리이고 ACES는 밝은 쪽을 압축하므로, 노출을 올리면 위쪽 색이 서로 붙는다.
 * 그건 "색을 바꿨는데 화면에서 구별이 안 되는" 상태 = 원칙 2의 정면 위반이다.
 * **광량을 올릴 거면 노출을 같이 내려야 한다.**
 *
 * ## 해상도를 깎는 후처리는 금지다
 *
 * 픽셀화·컬러 양자화·저해상도 렌더 타깃은 이 게임에서 스타일이 아니라 버그다.
 * 술래는 한 칸(0.25m) 어긋난 소품을 눈으로 찾아야 하는데, 화면 정보를 뭉개는 순간
 * 그 변화가 물리적으로 찾을 수 없게 된다. `devicePixelRatio` 상한(성능)만 걸고 그 외에는 손대지 않는다.
 */

import * as THREE from 'three'

import { MS_PER_SECOND } from '../core/time'
import { CELL_SIZE_METERS, type RoomBounds } from '../core/types'

/* --- 씬 전역 -------------------------------------------------------------------- */

/**
 * 먹색. gameHud.css의 --gh-ink(#191512)와 같은 값이다.
 * index.html의 body 배경·theme-color도 이 값이라 리사이즈·전환 중 색이 튀지 않는다.
 */
export const INK_HEX = 0x191512

const SCENE_BACKGROUND_COLOR = INK_HEX

/**
 * devicePixelRatio 상한.
 * 3배 이상 화면에서 원본 비율을 그대로 쓰면 픽셀 수가 9배가 되어 모바일 브라우저가 먼저 죽는다.
 * 모바일 접근성이 이 프로젝트의 최대 병목("6명 모으기")을 푸는 수단이므로 여기서 상한을 건다.
 */
const MAX_PIXEL_RATIO = 2

const HALF = 0.5

/* --- 아이소메트릭 투영 ----------------------------------------------------------- */

/**
 * 앙각 = atan(1/√2) ≈ 35.264°. 정통 아이소메트릭 각이다.
 *
 * 이 각에서만 월드 세 축이 화면에서 정확히 같은 길이로 투영된다.
 * 그 성질이 게임에 직접 쓰인다 - 축마다 축척이 다르면 "x로 한 칸"과 "z로 한 칸"이
 * 화면에서 다른 크기로 보이고, 그러면 4.1절이 보장하려던 "1칸 이동 = 최소 소품 하나만큼 어긋남"이
 * 방향에 따라 흔들린다.
 */
export const ISO_ELEVATION_RADIANS = Math.atan(Math.SQRT1_2)

/** 방위각 0단계 = 45°. 정면(0°)이 아니라 모서리에서 봐야 두 벽면이 동시에 읽힌다. */
export const ISO_AZIMUTH_BASE_RADIANS = Math.PI / 4

/** 회전 한 단계 = 90°. 이 값이 곧 원칙 2의 집행 장치다(가려진 물건을 다른 각도에서 보게 한다). */
export const ISO_QUARTER_TURN_RADIANS = Math.PI / 2

/** 방위각 단계 수. `rot`의 4단계(4절)와 같은 이유로 4다 - 이산값이라야 조작이 예측 가능하다. */
export const ISO_DIRECTION_COUNT = 4

/**
 * 카메라 거리 = 방 대각선 × 이 배수.
 *
 * 직교 투영에서 거리는 **화면 크기에 영향을 주지 않는다**(그것이 직교의 정의다).
 * 오로지 근·원평면 안에 방이 들어오는지만 결정한다. 1.5면 타깃이 방 구석까지 밀려도
 * 카메라는 방 바깥에 남는다 - 카메라가 방 안으로 들어가면 근평면이 방을 잘라 먹는다.
 */
const CAMERA_DISTANCE_DIAGONAL_RATIO = 1.5

/**
 * 근·원평면이 방 앞뒤로 남기는 여유(방의 시선축 투영 길이 배수).
 *
 * 1.0이면 수학적으로 딱 맞는다(`isoViewAxisSpan` 유도 참고). 0.25를 더 두는 이유는 둘이다 -
 * 소품이 벽을 살짝 파고들거나 창틀 밖으로 튀어나온 경우, 그리고 위 거리 배수를 나중에
 * 건드렸을 때 절두체가 조용히 방을 자르지 않도록 하는 완충이다.
 */
const CAMERA_DEPTH_MARGIN = 1.25

/**
 * 근평면 하한(미터).
 *
 * 현재 상수 조합에서는 **도달하지 않는다** - `isoViewAxisSpan ≤ 대각선`이므로
 * near ≥ (1.5 − 1.25) × 대각선 = 0.25 × 대각선이고, 가장 작은 방(24셀 = 6m)에서도 2.6m다.
 * 거리·여유 배수를 나중에 뒤집었을 때 near가 0이나 음수가 되어 투영 행렬이 통째로 깨지는 것만 막는다.
 */
const CAMERA_MIN_NEAR_METERS = 0.05

/**
 * 카메라가 타깃에서 떨어져 있는 방향(단위 벡터). 카메라는 이 방향의 반대편을 바라본다.
 * `out`을 받는 이유는 매 프레임 도는 경로라서다 - 프레임당 Vector3 할당은 곧 GC 압력이다.
 */
export function isoOffsetDirection(azimuth: number, out = new THREE.Vector3()): THREE.Vector3 {
  const horizontal = Math.cos(ISO_ELEVATION_RADIANS)
  return out.set(
    Math.sin(azimuth) * horizontal,
    Math.sin(ISO_ELEVATION_RADIANS),
    Math.cos(azimuth) * horizontal,
  )
}

/** 화면 오른쪽에 해당하는 월드 방향(단위 벡터). 앙각과 무관하게 항상 수평면 위에 있다. */
export function isoScreenRight(azimuth: number, out = new THREE.Vector3()): THREE.Vector3 {
  return out.set(Math.cos(azimuth), 0, -Math.sin(azimuth))
}

/** 화면 위쪽에 해당하는 월드 방향(단위 벡터). */
export function isoScreenUp(azimuth: number, out = new THREE.Vector3()): THREE.Vector3 {
  const sinElevation = Math.sin(ISO_ELEVATION_RADIANS)
  return out.set(
    -Math.sin(azimuth) * sinElevation,
    Math.cos(ISO_ELEVATION_RADIANS),
    -Math.cos(azimuth) * sinElevation,
  )
}

/** 방의 실제 크기(미터). 셀 단위 `RoomBounds`를 렌더 좌표로 옮긴 값이다. */
export type RoomWorldSize = Readonly<{ width: number; height: number; depth: number }>

/**
 * 방 바운딩 박스를 `axis` 방향으로 투영했을 때의 절반 길이(지지 함수).
 * 박스가 축 정렬이므로 각 축 성분의 절댓값 가중합으로 정확히 떨어진다 - 근사가 아니다.
 */
export function isoHalfExtent(size: RoomWorldSize, axis: THREE.Vector3): number {
  return (
    (Math.abs(axis.x) * size.width +
      Math.abs(axis.y) * size.height +
      Math.abs(axis.z) * size.depth) *
    HALF
  )
}

/** 방이 화면에서 차지하는 크기의 절반(월드 단위). 줌 하한(=방 전체 조망)의 근거값이다. */
export type IsoProjectedExtents = Readonly<{ halfWidth: number; halfHeight: number }>

export function isoProjectedHalfExtents(
  size: RoomWorldSize,
  azimuth: number,
): IsoProjectedExtents {
  return {
    halfWidth: isoHalfExtent(size, isoScreenRight(azimuth)),
    halfHeight: isoHalfExtent(size, isoScreenUp(azimuth)),
  }
}

/* --- 렌더러 색 파이프라인 --------------------------------------------------------- */

/**
 * 톤매핑 노출. **1.0을 상한으로 취급한다** - 근거는 파일 상단 "톤매핑" 절.
 */
const TONE_MAPPING_EXPOSURE = 1

/* --- 부트스트랩 조명 ------------------------------------------------------------- */

/**
 * `createScene`이 붙이는 임시 광원의 이름. **`lighting.ts`와의 계약이다.**
 * `createLightingRig`가 이 이름으로 찾아 걷어내므로, 바꾸려면 양쪽을 같이 바꿔야 한다.
 */
export const BOOTSTRAP_LIGHT_NAME = 'light-bootstrap'

const BOOTSTRAP_LIGHT_SKY_COLOR = 0xfff1e0
const BOOTSTRAP_LIGHT_GROUND_COLOR = 0xbfae9c
const BOOTSTRAP_LIGHT_INTENSITY = 1.4

/* --- 렌더 루프 ------------------------------------------------------------------ */

/**
 * 프레임 델타 상한(초).
 * 탭을 전환했다 돌아오면 rAF 간격이 수 초로 벌어지는데, 그 값을 그대로 흘리면
 * 카메라 컨트롤러가 한 프레임에 방 밖으로 튀어나간다. 물리가 없으므로 잘라내도 잃는 게 없다.
 */
const MAX_FRAME_DELTA_SECONDS = 0.1

/* --- 방 크기 -------------------------------------------------------------------- */

/** 방 각 축의 최소 셀 개수. 0 이하는 난이도 설정이 아니라 데이터 오류다. */
const MIN_ROOM_CELLS = 1

/**
 * 렌더 컨텍스트. 렌더·입력 레이어 전체가 이 하나를 돌려 쓴다.
 *
 * `Readonly`인 것은 **컨테이너**뿐이다 - `ctx.camera = other` 같은 교체를 막을 뿐,
 * `ctx.camera.position.set(...)`처럼 Three 객체 내부를 제자리 변경하는 것은 의도된 사용법이다.
 *
 * `camera`가 `OrthographicCamera`로 못박혀 있는 것이 이 타입의 계약이다.
 * 유니온으로 열어 두면 "지금 원근인지 직교인지" 모르는 코드가 생기고,
 * 그 코드는 반드시 한쪽에서만 맞는 산수를 쓴다.
 */
export type SceneCtx = Readonly<{
  scene: THREE.Scene
  camera: THREE.OrthographicCamera
  renderer: THREE.WebGLRenderer
  raycaster: THREE.Raycaster
}>

/**
 * 방 크기 검증.
 * `RoomBounds`는 방 템플릿 데이터(외부 입력)에서 오므로 경계에서 막는다.
 * 0이나 소수가 통과하면 벽 지오메트리가 0 크기가 되거나 셀 격자와 어긋나는데,
 * 둘 다 화면에서는 "그냥 좀 이상한 방"으로 보여 원인 추적이 극도로 어려워진다.
 */
function assertValidBounds(bounds: RoomBounds): void {
  const isValid =
    Number.isInteger(bounds.sizeX) &&
    bounds.sizeX >= MIN_ROOM_CELLS &&
    Number.isInteger(bounds.sizeY) &&
    bounds.sizeY >= MIN_ROOM_CELLS &&
    Number.isInteger(bounds.sizeZ) &&
    bounds.sizeZ >= MIN_ROOM_CELLS

  if (isValid) return

  throw new RangeError(
    `RoomBounds의 각 축은 ${MIN_ROOM_CELLS} 이상의 정수여야 한다: ` +
      `(${bounds.sizeX}, ${bounds.sizeY}, ${bounds.sizeZ})`,
  )
}

/**
 * 셀 단위 방 크기 → 미터. 렌더 레이어의 유일한 방 크기 변환 지점이다.
 * `grid.ts`의 `cellToWorld`와 같은 상수를 쓰므로 셀 격자와 셸이 어긋날 수 없다.
 */
export function roomWorldSize(bounds: RoomBounds): RoomWorldSize {
  assertValidBounds(bounds)

  return {
    width: bounds.sizeX * CELL_SIZE_METERS,
    height: bounds.sizeY * CELL_SIZE_METERS,
    depth: bounds.sizeZ * CELL_SIZE_METERS,
  }
}

/**
 * 방 중심의 월드 좌표.
 * 방의 원점은 셀 (0,0,0)의 모서리 = 월드 (0,0,0)이므로 중심은 항상 크기의 절반이다.
 * 카메라 초기 포즈·조명 타깃·팬 클램프의 기준점이 전부 이 한 점을 공유해야 시점이 흔들리지 않는다.
 */
export function roomCenterWorld(bounds: RoomBounds): THREE.Vector3 {
  const size = roomWorldSize(bounds)
  return new THREE.Vector3(size.width * HALF, size.height * HALF, size.depth * HALF)
}

/**
 * 월드 한 점 → 클라이언트(CSS 픽셀) 좌표. `tapPicker.toNdc`의 **역방향**이다.
 *
 * 여기 있는 이유는 `toNdc`가 여기 없는 이유와 같다 - 화면 ↔ 월드 변환은 카메라와 캔버스 사각형을
 * 함께 알아야 하고, 그 둘을 쥔 층이 렌더다. 세션이 이 산수를 스스로 하면 `tapPicker`의 것과
 * 반드시 한쪽만 맞게 갈린다. 처음 쓴 자리는 매치 세션의 "남이 찾은 자리에 이름표"다 -
 * 남의 탭 좌표는 오지 않으므로(11.2절) 물건의 월드 중심에서 화면 자리를 거꾸로 구한다.
 */
export function projectToClient(
  ctx: SceneCtx,
  point: Readonly<{ x: number; y: number; z: number }>,
): Readonly<{ x: number; y: number }> {
  const rect = ctx.renderer.domElement.getBoundingClientRect()
  const ndc = new THREE.Vector3(point.x, point.y, point.z).project(ctx.camera)
  return {
    x: rect.left + ((ndc.x + 1) * HALF) * rect.width,
    y: rect.top + ((1 - ndc.y) * HALF) * rect.height,
  }
}

/** 방 대각선(미터). 카메라 거리·원평면의 공통 척도. */
function roomDiagonal(size: RoomWorldSize): number {
  return Math.hypot(size.width, size.height, size.depth)
}

/**
 * 카메라와 타깃 사이의 거리(미터).
 * 직교 투영이라 화면 크기와 무관하며, 근·원평면 안에 방이 들어오게 하는 역할만 한다.
 * `isoCamera.ts`가 카메라를 배치할 때 같은 값을 써야 원평면 계산이 맞으므로 공개한다.
 */
export function isoCameraDistance(bounds: RoomBounds): number {
  return roomDiagonal(roomWorldSize(bounds)) * CAMERA_DISTANCE_DIAGONAL_RATIO
}

/**
 * 방을 **시선축에 투영했을 때의 최대 길이**(미터). 4방향 어디서 봐도 이 값을 넘지 않는다.
 *
 * 근·원평면을 얼마나 좁힐 수 있는가가 여기서 결정되므로 유도를 남긴다.
 * 시선축은 `isoOffsetDirection(az)` = `(sin az·cosE, sinE, cos az·cosE)`이고,
 * 축 정렬 박스를 단위벡터 `a`에 투영한 길이는 `|aₓ|w + |a_y|h + |a_z|d`다(지지 함수).
 * 대입하면 `cosE(|sin az|·w + |cos az|·d) + sinE·h`이고, 방위각에 대한 최댓값은
 * `|sin az|w + |cos az|d ≤ hypot(w, d)`에서 나온다. 아래 식이 그 최댓값이다.
 *
 * **깊이 범위가 이 값의 두 배면 충분한 이유**: 시점(`target`)도 방 안에 갇혀 있으므로
 * (`isoCamera.ts`의 `clampTarget`), 방의 어떤 점과 시점의 시선축 성분 차이는 이 값을 넘지 못한다.
 * 카메라는 시점에서 `isoCameraDistance`만큼 떨어져 있으니, 깊이는 그 거리 ± 이 값 안에 전부 들어온다.
 * **줌·팬은 이 범위를 넓히지 않는다** - 직교 투영에서 줌은 깊이를 건드리지 않고,
 * 팬은 화면 기저(오른쪽·위) 성분만 움직여 시선축 성분과 직교하기 때문이다.
 */
export function isoViewAxisSpan(size: RoomWorldSize): number {
  return (
    Math.cos(ISO_ELEVATION_RADIANS) * Math.hypot(size.width, size.depth) +
    Math.sin(ISO_ELEVATION_RADIANS) * size.height
  )
}

/**
 * 직교 카메라의 근·원평면(미터).
 *
 * **직교 카메라는 방 밖에서 보므로 near를 크게 잡을 수 있다** - 이게 z-fighting 대책의 전부다.
 * 예전에는 `near = 0.05`, `far = 거리×2 + 대각선`이라 20m 방에서 깊이 범위가 113m였다.
 * 이 식은 같은 방에서 62m로 좁힌다.
 *
 * **줄어드는 것은 near:far 비가 아니라 far-near 구간이고, 여기서 중요한 것도 그쪽이다.**
 * 원근 카메라라면 깊이 정밀도가 near에 몰려 있어 비가 문제가 되지만, 직교 투영의 깊이는
 * **선형**이라 정밀도가 구간 전체에 고르게 퍼진다(로그 깊이 버퍼도 쓰지 않는다).
 * 그래서 24비트 기준 한 칸의 크기는 `(far - near) / 2^24` 하나로 정해진다 -
 * 20m 방에서 6.78µm(113m 구간) → 3.70µm(62m 구간), 즉 **1.8배(0.87비트)** 좋아진다.
 * 비를 근거로 삼으면 "1:2272를 1:6으로 줄였으니 수백 배"라고 읽게 되는데 그건 사실이 아니다.
 * 이 값을 다시 만질 사람이 기대치를 정확히 세울 수 있도록 근거를 구간 쪽에 못박아 둔다.
 *
 * 1.8배로 충분한 이유는 자글거림의 정체가 **간격 0인 동일평면 쌍이 아니라 아주 얇은 간격**이라서다.
 * 실제로 택배상자(`cardboard-box-open`)를 최대 줌으로 놓고 카메라를 시선축으로 밀어 재 보면
 * 뒤집히는 픽셀이 27개 → 7개(47만 픽셀 중)로 줄어 눈에 보이는 깜빡임이 사라진다.
 *
 * 좁히다가 방이 잘리면 "오브젝트가 자글거린다"보다 훨씬 큰 사고이므로,
 * 여유는 상수가 아니라 **방 크기에 비례**하게 잡는다(`CAMERA_DEPTH_MARGIN`).
 * 레벨마다 방이 24~80셀로 달라지는데 고정 미터 여유를 쓰면 작은 방에서만 과하게 남는다.
 */
export function isoDepthRange(bounds: RoomBounds): Readonly<{ near: number; far: number }> {
  const halfRange = isoViewAxisSpan(roomWorldSize(bounds)) * CAMERA_DEPTH_MARGIN
  const distance = isoCameraDistance(bounds)

  return {
    near: Math.max(distance - halfRange, CAMERA_MIN_NEAR_METERS),
    far: distance + halfRange,
  }
}

/* --- 카메라 --------------------------------------------------------------------- */

/**
 * 종횡비를 절두체에 반영한다. **세로(`top`/`bottom`)가 기준이고 가로가 따라간다.**
 *
 * 원근 카메라의 `aspect` 한 줄에 대응하는 자리이며, 직교에서 이게 다르다는 것이
 * 이번 전환에서 가장 조용히 깨지기 쉬운 지점이다. `aspect`를 그냥 두고 리사이즈하면
 * 화면만 늘어나고 절두체는 그대로라 **월드가 가로로 늘어난다.**
 *
 * 세로를 기준으로 삼은 이유: 세로 절두체가 고정이면 `camera.zoom`의 의미가
 * "화면 세로에 담기는 월드 높이"로 고정되어, 리사이즈해도 줌 감각이 유지된다.
 * 가로로 좁은 폰에서 방이 좌우로 잘리는 문제는 줌 하한이 따로 막는다(`isoCamera.ts`).
 */
export function applyIsoAspect(camera: THREE.OrthographicCamera, aspect: number): void {
  camera.right = camera.top * aspect
  camera.left = -camera.right
  camera.updateProjectionMatrix()
}

/**
 * 직교 카메라를 방 크기에 맞춘다. 절두체·근원평면·초기 포즈를 전부 여기서 정한다.
 *
 * 세로 절두체를 "정사각 화면에서 방이 딱 들어오는 크기"로 잡으므로 **`camera.zoom === 1`이
 * 곧 전체 조망**이 된다. 줌 값이 방 크기와 무관한 숫자가 되면 `isoCamera.ts`의 정규화 줌이
 * 매직넘버 덩어리가 되므로, 기준을 여기서 한 번 정해 준다.
 *
 * 공개하는 이유는 테스트다 - `createScene`은 WebGL을 요구해서 jsdom에서 만들 수 없는데,
 * 카메라만 따로 세울 수 없으면 아이소 카메라 조작을 단위 테스트할 방법이 사라진다.
 */
export function configureIsoCamera(
  camera: THREE.OrthographicCamera,
  bounds: RoomBounds,
  aspect: number,
): void {
  const size = roomWorldSize(bounds)
  const center = roomCenterWorld(bounds)
  const extents = isoProjectedHalfExtents(size, ISO_AZIMUTH_BASE_RADIANS)
  const distance = isoCameraDistance(bounds)

  camera.top = Math.max(extents.halfHeight, extents.halfWidth)
  camera.bottom = -camera.top
  camera.zoom = 1

  // 타깃이 방 구석까지 밀려도(줌·팬·회전 어디서든) 방 전체가 근·원평면 사이에 남는다.
  // 유도는 `isoViewAxisSpan`·`isoDepthRange` 참고 - 좁힐수록 깊이 정밀도가 좋아진다.
  const depth = isoDepthRange(bounds)
  camera.near = depth.near
  camera.far = depth.far

  camera.up.set(0, 1, 0)
  camera.position
    .copy(center)
    .addScaledVector(isoOffsetDirection(ISO_AZIMUTH_BASE_RADIANS), distance)
  camera.lookAt(center)

  applyIsoAspect(camera, aspect)
}

/* --- 렌더러 --------------------------------------------------------------------- */

/**
 * WebGL 컨텍스트 생성.
 * 실패는 흔하다(하드웨어 가속 꺼짐, 컨텍스트 개수 초과, 구형 브라우저).
 * Three가 던지는 원문은 원인 파악에 도움이 안 되므로 사용자에게 보여줄 수 있는 문장으로 감싸되,
 * `cause`로 원본을 보존해 조용히 삼키지 않는다.
 */
function createRenderer(canvas: HTMLCanvasElement): THREE.WebGLRenderer {
  try {
    return new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      // 배경을 씬이 칠하므로 알파 버퍼가 필요 없다. 끄는 쪽이 합성 비용이 싸다.
      alpha: false,
      powerPreference: 'high-performance',
    })
  } catch (error) {
    throw new Error(
      'WebGL 컨텍스트를 만들지 못했다. 브라우저의 하드웨어 가속이 꺼져 있거나 WebGL이 차단된 상태일 수 있다.',
      { cause: error },
    )
  }
}

/** devicePixelRatio는 환경에 따라 없거나 비정상값일 수 있다. 경계에서 정상화한다. */
function currentPixelRatio(): number {
  const rawRatio = window.devicePixelRatio
  if (!Number.isFinite(rawRatio) || rawRatio <= 0) return 1
  return Math.min(rawRatio, MAX_PIXEL_RATIO)
}

/**
 * 캔버스의 CSS 크기에 렌더 버퍼와 절두체 종횡비를 맞춘다. 변경이 있었으면 `true`.
 *
 * `window.addEventListener('resize')` 대신 **매 프레임 비교** 방식을 쓴다. 이유:
 *  - 리스너 방식은 창 크기 변경만 잡는다. 브라우저 확대, 모니터 간 이동(devicePixelRatio 변경),
 *    모바일 주소창 접힘, 화면 회전은 resize 이벤트가 없거나 신뢰할 수 없다.
 *    **모바일이 주 타깃이므로 주소창 접힘·회전을 놓치면 곧바로 화면이 늘어난다.**
 *  - 등록할 리스너가 없으므로 `startRenderLoop`가 돌려주는 정리 함수가
 *    "rAF 취소" 하나로 끝난다 - 해제를 잊어 생기는 누수 자체가 존재하지 않는다.
 *
 * `setSize(..., false)`인 것은 캔버스 CSS 크기를 index.html이 이미 100vw/100vh로 잡고 있어서다.
 * Three가 style을 덮어쓰면 레이아웃 주인이 둘이 되어 리사이즈 루프가 생긴다.
 */
export function syncRendererToDisplaySize(ctx: SceneCtx): boolean {
  const canvas = ctx.renderer.domElement
  const cssWidth = canvas.clientWidth
  const cssHeight = canvas.clientHeight

  // 레이아웃 이전이거나 캔버스가 숨겨진 순간에는 0이 나온다.
  // 그대로 쓰면 종횡비가 0 또는 NaN이 되어 투영 행렬 전체가 오염된다.
  // 오류가 아니라 일시 상태이므로 건너뛴다.
  if (cssWidth === 0 || cssHeight === 0) return false

  const pixelRatio = currentPixelRatio()
  const targetWidth = Math.floor(cssWidth * pixelRatio)
  const targetHeight = Math.floor(cssHeight * pixelRatio)

  if (canvas.width === targetWidth && canvas.height === targetHeight) return false

  ctx.renderer.setPixelRatio(pixelRatio)
  ctx.renderer.setSize(cssWidth, cssHeight, false)
  applyIsoAspect(ctx.camera, cssWidth / cssHeight)

  return true
}

/**
 * 정식 조명 리그가 붙기 전까지 쓰는 임시 채움광 하나를 붙인다.
 * 유일한 목적은 **조립 중간 상태가 완전한 암흑이 되지 않게 하는 것**이며, 룩을 만들려는 값이 아니다.
 */
function addBootstrapLight(scene: THREE.Scene): void {
  const light = new THREE.HemisphereLight(
    BOOTSTRAP_LIGHT_SKY_COLOR,
    BOOTSTRAP_LIGHT_GROUND_COLOR,
    BOOTSTRAP_LIGHT_INTENSITY,
  )
  light.name = BOOTSTRAP_LIGHT_NAME
  scene.add(light)
}

/**
 * 렌더러의 색 파이프라인과 그림자맵을 설정한다.
 *
 * 색공간을 명시하는 이유: 기본값이 이미 sRGB지만, 이 값이 바뀌면 팔레트 사다리(`palette.ts`)의
 * 명암비 계산이 통째로 무의미해진다. 기본값에 기대지 않고 계약으로 못박는다.
 */
function configureRenderer(renderer: THREE.WebGLRenderer): void {
  renderer.outputColorSpace = THREE.SRGBColorSpace

  renderer.toneMapping = THREE.ACESFilmicToneMapping
  renderer.toneMappingExposure = TONE_MAPPING_EXPOSURE

  renderer.shadowMap.enabled = true
  // three r185에서 `PCFSoftShadowMap`이 **폐기됐다.** 지정해도 첫 그림자 렌더에서 조용히
  // `PCFShadowMap`으로 바뀌고 경고만 남는다. 실제로 도는 값을 그대로 적어 코드와 화면을 일치시킨다.
  renderer.shadowMap.type = THREE.PCFShadowMap
}

/**
 * 씬·카메라·렌더러·레이캐스터를 만들어 하나로 묶는다.
 *
 * `bounds`를 받는 이유: 절두체가 방 크기에 종속이기 때문이다.
 * 방 크기를 모른 채 만들면 줌 1이 무엇을 뜻하는지 정의되지 않는다.
 *
 * 광원은 여기서 만들지 않는다. 호출부는 평면도가 준비되는 대로
 * `createLightingRig(ctx.scene, plan)`(`lighting.ts`)을 불러야 하며,
 * 그때 여기서 붙인 부트스트랩 광원이 자동으로 걷힌다.
 */
export function createScene(canvas: HTMLCanvasElement, bounds: RoomBounds): SceneCtx {
  assertValidBounds(bounds)

  const renderer = createRenderer(canvas)
  configureRenderer(renderer)

  const scene = new THREE.Scene()
  scene.name = 'weird-room'
  scene.background = new THREE.Color(SCENE_BACKGROUND_COLOR)

  const camera = new THREE.OrthographicCamera()
  camera.name = 'main-camera'
  // 실제 종횡비는 아래 `syncRendererToDisplaySize`가 곧바로 덮는다.
  // 여기서 1로 두는 것은 첫 프레임 전에 절두체가 0 크기로 남지 않게 하기 위해서다.
  configureIsoCamera(camera, bounds, 1)

  addBootstrapLight(scene)

  const ctx: SceneCtx = { scene, camera, renderer, raycaster: new THREE.Raycaster() }

  // 루프 시작 전에 한 번 맞춰 둔다. 첫 프레임 전에 수동으로 render()를 부르는 호출부도 정상 동작해야 한다.
  syncRendererToDisplaySize(ctx)

  return ctx
}

/**
 * requestAnimationFrame 루프를 시작하고 **정지 함수**를 돌려준다.
 *
 * 매 프레임 순서: 표시 크기 동기화 → `onFrame(dt)` → 렌더.
 * `onFrame`이 먼저인 이유는 카메라 컨트롤러가 이번 프레임의 카메라를 갱신한 뒤 그려야 한 프레임 지연이 없어서다.
 *
 * 돌려주는 함수는 **루프만** 세운다. GPU 리소스 해제(`renderer.dispose()`, 지오메트리·머티리얼)는
 * 소유자가 각자 한다 - 루프 정지와 리소스 해제는 수명이 다르다(일시정지 후 재개가 가능해야 한다).
 */
export function startRenderLoop(ctx: SceneCtx, onFrame: (dt: number) => void): () => void {
  let frameHandle: number | null = null
  let previousTimestamp: number | null = null

  // 정리 함수는 멱등해야 한다. 호출부가 언마운트 경로에서 두 번 부르는 일은 흔하다.
  const stop = (): void => {
    if (frameHandle === null) return
    cancelAnimationFrame(frameHandle)
    frameHandle = null
  }

  const tick = (timestamp: number): void => {
    frameHandle = requestAnimationFrame(tick)

    const elapsedMs = previousTimestamp === null ? 0 : timestamp - previousTimestamp
    const deltaSeconds = Math.min(elapsedMs / MS_PER_SECOND, MAX_FRAME_DELTA_SECONDS)
    previousTimestamp = timestamp

    syncRendererToDisplaySize(ctx)

    try {
      onFrame(deltaSeconds)
      ctx.renderer.render(ctx.scene, ctx.camera)
    } catch (error) {
      // 예외를 삼키면 화면만 멈추고 원인은 사라진다. 반대로 그냥 두면 초당 60번 같은 예외가 쏟아져
      // 콘솔이 마비되고 최초 발생 프레임을 못 찾는다. 그래서 루프를 세운 뒤 원본 예외를 다시 던진다.
      stop()
      console.error('[render] 프레임 처리 중 예외가 발생해 렌더 루프를 정지한다.', error)
      throw error
    }
  }

  frameHandle = requestAnimationFrame(tick)

  return stop
}
